import { Suspense } from "react";
import { Link } from "expo-router";
import { Button, Table } from "@ptsd/uikit";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { Dialog, ScrollView, Text, YStack } from "tamagui";

import { api } from "~/utils/api";
import type { RouterOutputs } from "~/utils/api";
import { dateToDDMMYY } from "~/utils/date-to-readable";

type SlicesList = NonNullable<
  RouterOutputs["research"]["getResearchByUserId"]
>[number];

const columnHelper = createColumnHelper<SlicesList>();

const columns = [
  columnHelper.accessor("createdAt", {
    header: "Запрошений",
    cell: (info) => dateToDDMMYY(info.getValue()),
  }),

  columnHelper.accessor("researchTypeName", {
    header: "Дослідження",
    cell: (info) => info.getValue(),
  }),

  columnHelper.accessor("name", {
    header: "Назва дослідження",
    cell: (info) => info.getValue(),
  }),
  columnHelper.accessor((row) => ({ id: row.id }), {
    header: "Керування",
    cell: (info) => {
      const { id, type } = info.row.original;
      return (
        <Link href={`research/${type}/${id}/results`} asChild>
          <Button
            width={"100%"}
            height={"$4"}
            variant={"outlined"}
            size={"sm"}
            rounded="lg"
            paddingHorizontal={"$2"}
          >
            Переглянути
          </Button>
        </Link>
      );
    },
    enableSorting: false,
    enableHiding: false,
    id: "linkToResearch",
  }),

  columnHelper.accessor("status", {
    header: "Статус",
    cell: (info) => info.getValue(),
  }),
];

export const SlicesListDialog: React.FC<{
  open: boolean | undefined;
  onOpenChange: (open: boolean) => void;
  userId: string | undefined;
}> = ({ open, onOpenChange, userId }) => {
  if (!userId) return null;

  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <Dialog.Portal>
        <Dialog.Overlay
          style={{
            backgroundColor: "#6A696966",
            backdropFilter: "blur(10px)",
          }}
        />
        <Dialog.Content
          borderColor={"$gray-600"}
          borderWidth={"$0.2"}
          maxHeight={673}
          maxWidth={1148}
          width={"100%"}
          backgroundColor={"white"}
          paddingVertical={"$5"}
          paddingHorizontal={"$8"}
          borderRadius={"$5"}
        >
          <YStack gap={"$0.5"} paddingBottom={"$3"}>
            <Dialog.Title
              fontFamily={"$subtitle"}
              fontWeight={"$6"}
              fontSize={"$7"}
              lineHeight={"$7"}
              textAlign={"center"}
              color={"$gray-primary-900"}
            >
              Список досліджень учасника
            </Dialog.Title>
          </YStack>
          <Suspense>
            <SlicesListDialogTable userId={userId} />
          </Suspense>
          <Dialog.Close asChild>
            <Button
              marginTop={"$4"}
              alignSelf={"center"}
              rounded="lg"
              variant="outlined"
              size="md"
              minWidth={240}
            >
              Закрити
            </Button>
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog>
  );
};

const SlicesListDialogTable: React.FC<{
  userId: string;
}> = ({ userId }) => {
  const [data] = api.research.getResearchByUserId.useSuspenseQuery({ userId });

  const table = useReactTable({
    data: data ?? [],
    columns: columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const headerGroups = table.getHeaderGroups();
  const tableRows = table.getRowModel().rows;
  return (
    <ScrollView maxHeight={414}>
      <Table
        alignCells={{ x: "center", y: "center" }}
        alignHeaderCells={{ y: "center", x: "center" }}
        cellHeight="$5.8"
        data-id="users-table"
      >
        <Table.Head data-id="users-table-head">
          {headerGroups.map((headerGroup, index) => {
            return (
              <Table.Row
                data-id="users-table-header-row"
                rowLocation={
                  index === 0
                    ? "first"
                    : index === headerGroups.length - 1
                      ? "last"
                      : "middle"
                }
                key={headerGroup.id}
                justifyContent="flex-start"
              >
                {headerGroup.headers.map((header, index) => {
                  return (
                    <Table.HeaderCell
                      data-id={"users-table-header-cell-" + header.id}
                      backgroundColor="white"
                      key={header.id}
                      justifyContent="flex-start"
                      borderWidth={0}
                      cellLocation={
                        index === 0
                          ? "first"
                          : index === headerGroup.headers.length - 1
                            ? "last"
                            : "middle"
                      }
                      maxWidth={header.id === "select" ? 40 : undefined}
                    >
                      <Text
                        fontFamily={"$subtitle"}
                        fontSize={"$2"}
                        lineHeight={"$2"}
                        fontWeight={"$5"}
                        color={"$gray-primary-900"}
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                      </Text>
                    </Table.HeaderCell>
                  );
                })}
              </Table.Row>
            );
          })}
        </Table.Head>

        <Table.Body data-id="users-table-body">
          {tableRows.length === 0 ? (
            <Table.Row
              data-id="users-table-row"
              borderBottomRightRadius={"$1"}
              borderBottomLeftRadius={"$1"}
              backgroundColor={"white"}
              key={1}
            >
              <Table.Cell
                data-id="users-table-no-data-cell"
                borderColor={"$gray-200"}
                width={"100%"}
                key={1}
                justifyContent="center"
              >
                <Text
                  numberOfLines={1}
                  ellipsizeMode="tail"
                  fontFamily={"$subtitle"}
                  fontSize={"$2"}
                  lineHeight={"$2"}
                  color={"$gray-primary-900"}
                >
                  Немає даних
                </Text>
              </Table.Cell>
            </Table.Row>
          ) : (
            tableRows?.map((row, index) => {
              return (
                <Table.Row
                  data-id="users-table-row"
                  backgroundColor={index % 2 === 0 ? "white" : "$gray-50"}
                  selected={row.getIsSelected()}
                  rowLocation={
                    index === 0
                      ? "first"
                      : index === tableRows.length - 1
                        ? "last"
                        : "middle"
                  }
                  key={row.id}
                >
                  {row.getVisibleCells().map((cell, index) => {
                    return (
                      <Table.Cell
                        data-id={"users-table-cell-" + cell.id}
                        justifyContent="flex-start"
                        cellLocation={
                          index === 0
                            ? "first"
                            : row.getVisibleCells().length - 1 === index
                              ? "last"
                              : "middle"
                        }
                        key={cell.id}
                        flex={1}
                        maxWidth={cell.column.id === "select" ? 40 : undefined}
                      >
                        <Text
                          width={"100%"}
                          fontFamily={"$subtitle"}
                          lineHeight={"$2"}
                          fontSize={"$2"}
                          fontWeight={"$4"}
                          color={"$gray-primary-900"}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </Text>
                      </Table.Cell>
                    );
                  })}
                </Table.Row>
              );
            })
          )}
        </Table.Body>
      </Table>
    </ScrollView>
  );
};
